import React from 'react';

import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { AnimatePresence } from 'framer-motion';

import { IconChevronDown } from '@pkg/assets/icons';

import { CurrencyIcon } from '../CurrencyIcon';

import { currencyName } from './CurrencySelector.data';
import { tokens } from './CurrencySelector.tokens';
import { Menu, type MenuProps } from './components';
import { useCurrencySelectorPopover } from './useCurrencySelectorPopover';

import {
    Action,
    ArrowWrapper,
    Details,
    IconWrapper,
    Label,
    MenuPopover,
    Name,
    StyledCurrencySelector,
    Symbol,
} from './CurrencySelector.style';

export interface CurrencySelectorProps extends MenuProps {
    label?: string;
    ariaLabel?: string;
    baseHeight?: number;
    disabled?: boolean;
    compact?: boolean;
    glass?: boolean;
}

const DEFAULT_LABEL = 'Base currency';
const DEFAULT_ARIA_LABEL = 'Select a different currency';

export function CurrencySelector({
    label = DEFAULT_LABEL,
    ariaLabel = DEFAULT_ARIA_LABEL,
    selected = 'usd',
    baseHeight = tokens.baseHeight,
    disabled = false,
    compact = false,
    glass = false,
    onSelect,
}: CurrencySelectorProps) {
    const {
        open,
        onOpenChange,
        context,
        setReference,
        setFloating,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
    } = useCurrencySelectorPopover();

    const handleSelect: CurrencySelectorProps['onSelect'] = (currency) => {
        onOpenChange(false);
        onSelect(currency);
    };

    const dropdownMarkup = open ? (
        <FloatingPortal>
            <FloatingFocusManager context={context} modal={false}>
                <MenuPopover
                    $baseHeight={baseHeight}
                    $compact={compact}
                    ref={setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    <Menu selected={selected} onSelect={handleSelect} />
                </MenuPopover>
            </FloatingFocusManager>
        </FloatingPortal>
    ) : null;

    const detailsMarkup = compact ? null : (
        <Details>
            <Label>{label}</Label>
            <Name>{currencyName[selected]}</Name>
        </Details>
    );

    return (
        <StyledCurrencySelector $baseHeight={baseHeight} $compact={compact} $glass={glass}>
            <Action
                ref={setReference}
                aria-label={ariaLabel}
                disabled={disabled ? true : undefined}
                {...getReferenceProps()}
            >
                <Symbol>
                    <IconWrapper>
                        <CurrencyIcon currency={selected} theme="dark" />
                    </IconWrapper>
                </Symbol>

                {detailsMarkup}

                <ArrowWrapper>
                    <IconChevronDown />
                </ArrowWrapper>
            </Action>

            <AnimatePresence>{dropdownMarkup}</AnimatePresence>
        </StyledCurrencySelector>
    );
}
